import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { SearchSchema, allowedSearchSyncUpdateTypes, extractAllowedTypes, toElasticQuery, } from '@alliance-disposal/wql';
import axios from 'axios';
import { parseAsInteger, parseAsString, useQueryState, useQueryStates } from 'nuqs';
import { buildSearchResponse, countFilterFields, createEmptyEsResponse, createSourQueryParser, extractTotalHitsFromEsResponse, handleCheckIfEqualToDefault, handlePrepareQuery, handleTransformAttributesToFilters, isNumberStrict, removeUnusedFiltersAndConvertToArray, transformArrayToEsResponse, } from '../helpers/functions';
import { SourSearchAppContext } from './app-context';
// Define the initial state of the context
const initialValue = {
    environmentOptions: {
        searchApiBaseUrl: '',
        apiKey: '',
        application: 'aap',
        environment: 'development',
    },
    defaultFilters: undefined,
    updateDefaultFilters: () => { },
    isLoading: false,
    filtersOpen: false,
    highlightActive: false,
    setFiltersOpen: () => { },
    onSubmit: () => { },
    onNavigate: () => { },
    onSeeAllClick: () => { },
    page: 0,
    size: 50,
    searchResponse: undefined,
    showEasyFilter: false,
    searchTerm: '',
    setSearchTerm: () => { },
    onGlobalOnSeeAllClick: () => { },
    filterRestrictions: [],
    sourQueryStatus: {
        searchActive: false,
        filterActive: false,
        searchOrFilterActive: false,
        sourQuery: {
            query: {
                query: '',
                restrictToFields: [],
            },
            filters: {},
            sort: { field: undefined, direction: 'DESC' },
            type: undefined,
        },
        activeSourQuery: undefined,
        error: null,
    },
    handlePaginationChange: () => { },
    filterableAttributes: [],
    getAttributeAsFilter: () => {
        return {
            field: '',
            type: 'STRING',
            operator: 'IS_EQUAL_TO',
            value: '',
            isExpanded: false,
            fieldName: '',
        };
    },
    handleSourSearchSetup: () => {
        return {
            type: undefined,
            query: {
                query: '',
                restrictToFields: [],
            },
            filters: {},
            sort: { field: undefined, direction: 'DESC' },
        };
    },
    handleUpdateSourQuery: () => {
        return {
            type: undefined,
            query: {
                query: '',
                restrictToFields: [],
            },
        };
    },
    focusSearch: () => { },
    searchInputRef: { current: null },
    rerunLastSearch: () => { },
};
// Create the context
export const SourSearchContext = createContext(initialValue);
// Create a provider component
export const SourSearchProvider = ({ children, onNavigate: externalNavigate, restrictTo, highlightActive, createQueryParams, onSeeAllClick, initialValues, showEasyFilter = false, onBackendProxyRequest, accessFilters: providerAccessFilters, }) => {
    const { environmentOptions, globalRestrictTo, onGlobalOnSeeAllClick, accessFilters: appAccessFilters, } = useContext(SourSearchAppContext);
    const restriction = restrictTo || globalRestrictTo || undefined;
    const searchInputRef = useRef(null);
    // Ref to store latest tab param for immediate access (avoids stale value from useQueryState)
    const tabParamRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [sourQueryStatus, setSourQueryStatus] = useState(initialValue.sourQueryStatus);
    // New
    const [page, setPage] = useState(initialValues?.page || initialValue.page);
    const [size, setSize] = useState(initialValues?.size || initialValue.size);
    const [searchResponse, setSearchResponse] = useState(undefined);
    const [defaultFilters, setDefaultFilters] = useState(initialValues?.defaultFilters);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRestrictions, setFilterRestrictions] = useState(initialValue.filterRestrictions);
    const [filterableAttributes, setFilterableAttributes] = useState([]);
    // State for handlers
    const [onResultsHandler, setOnResultsHandler] = useState();
    const registerOnResultsHandler = useCallback((handler) => {
        setOnResultsHandler(() => handler);
    }, []);
    // Create the custom parser instance
    const sourQueryParser = createSourQueryParser({ application: environmentOptions?.application });
    // nuqs query state for SourQuery, page, and size
    const [queryParams, setQueryParams] = useQueryStates({
        sourQuery: {
            parse: sourQueryParser.parse,
            serialize: sourQueryParser.serialize,
            defaultValue: null,
        },
        searchPage: parseAsInteger.withDefault(initialValues?.page ?? initialValue.page),
        searchSize: parseAsInteger.withDefault(initialValues?.size ?? initialValue.size),
    });
    // Destructure for convenience
    const { sourQuery, searchPage: nuqsPage, searchSize: nuqsSize } = queryParams;
    // Read tab param from nuqs to get the latest value (not from URL which may be stale)
    const [tabParam] = useQueryState('tab', parseAsString);
    // Keep ref in sync with tabParam from nuqs
    useEffect(() => {
        if (tabParam) {
            tabParamRef.current = tabParam;
        }
    }, [tabParam]);
    const getAttributeAsFilter = (path) => {
        // Safely traverse the filters object using the path, but only return if the final value is a filter field object
        const keys = path.split('.');
        let current = sourQueryStatus.sourQuery.filters;
        for (const key of keys) {
            if (current == null || typeof current !== 'object')
                return undefined;
            current = current[key];
        }
        return current;
    };
    const handleSourSearchSetup = (type, query, options) => {
        if (!allowedSearchSyncUpdateTypes.includes(type) || !environmentOptions.application) {
            return {
                type,
            };
        }
        const excludedFilterFields = new Set(options?.excludedFilterFields ?? []);
        const attributes = [...SearchSchema[type].filterableAttributes].filter((attribute) => {
            if (attribute?.disabledOn && environmentOptions.application) {
                if (attribute.disabledOn.includes(environmentOptions.application)) {
                    return false;
                }
            }
            return !excludedFilterFields.has(attribute.field);
        });
        if (attributes)
            setFilterableAttributes([...attributes]);
        const newFilters = handleTransformAttributesToFilters(attributes, environmentOptions.application, query?.filters);
        const newQuery = {
            query: query?.query?.query || '',
            restrictToFields: query?.query?.restrictToFields || [],
        };
        const sortField = query?.sort?.field;
        const newSort = {
            field: sortField && !excludedFilterFields.has(sortField) ? sortField : undefined,
            direction: query?.sort?.direction || 'DESC',
        };
        const newSourQuery = {
            type,
            query: newQuery,
            filters: newFilters,
            sort: newSort,
        };
        setSourQueryStatus({
            ...sourQueryStatus,
            sourQuery: newSourQuery,
        });
        return newSourQuery;
    };
    const handleUpdateFilterField = (fieldPath, values) => {
        const keys = fieldPath.split('.');
        const lastKey = keys.at(-1);
        if (!lastKey) {
            console.warn(`Invalid fieldPath: ${fieldPath}`);
            return;
        }
        // Start with a shallow copy of the root filters object
        // Shallow copies ensure unchanged branches retain identity (prevents unnecessary React re-renders). Don't use structuredClone
        const newFilters = { ...sourQueryStatus.sourQuery.filters };
        // Traverse and shallow clone each nested level
        let currentOld = sourQueryStatus.sourQuery.filters;
        let currentNew = newFilters;
        for (let i = 0; i < keys.length - 1; i++) {
            const key = keys[i];
            if (!(key in currentOld)) {
                console.warn(`Path '${fieldPath}' not found at '${key}'`);
                return;
            }
            currentNew[key] = { ...currentOld[key] };
            currentOld = currentOld[key];
            currentNew = currentNew[key];
        }
        if (!(lastKey in currentOld)) {
            console.warn(`Field '${lastKey}' not found at path '${fieldPath}'`);
            return;
        }
        // Safely traverse the object tree to get the parent object
        let parent = newFilters;
        for (let i = 0; i < keys.length - 1; i++) {
            if (parent && typeof parent === 'object' && keys[i] in parent) {
                parent = parent[keys[i]];
            }
            else {
                parent = undefined;
                break;
            }
        }
        if (!(lastKey in currentOld)) {
            console.warn(`Field '${lastKey}' not found at path '${fieldPath}'`);
            return;
        }
        currentNew[lastKey] = {
            ...currentOld[lastKey],
            isExpanded: values.isExpanded ?? !!(values.operator || values.value),
            operator: values.isExpanded === false || values.operator === null
                ? undefined
                : (values.operator ?? currentOld[lastKey]?.operator ?? undefined),
            value: values.isExpanded === false ||
                values.value === null ||
                values.operator === null ||
                (!values.operator && !currentOld[lastKey]?.operator && currentOld[lastKey]?.type !== 'BOOLEAN')
                ? undefined
                : values.value,
        };
        return newFilters;
    };
    const handleUpdateSourQuery = (options) => {
        const newState = { ...sourQueryStatus.sourQuery };
        if (options.type) {
            newState.type = options.type;
        }
        else if (options.type === null) {
            newState.type = undefined;
        }
        if (options.query) {
            newState.query = options.query;
        }
        else if (options.query === null) {
            newState.query = {
                query: '',
                restrictToFields: [],
            };
        }
        if (options.sort) {
            newState.sort = options.sort;
        }
        else if (options.sort === null) {
            newState.sort = { field: undefined, direction: 'DESC' };
        }
        if (options.filters?.single) {
            const newFilters = handleUpdateFilterField(options.filters.single.fieldPath, options.filters.single.values);
            newState.filters = newFilters;
        }
        else if (options.filters?.all) {
            newState.filters = options.filters.all;
        }
        setSourQueryStatus({
            ...sourQueryStatus,
            sourQuery: newState,
        });
        return newState;
    };
    // --- handleCreateQueryParams logic extracted from handleSubmit ---
    // TODO CHANGE THIS TO REMOVE ANY EMPTY FILTERS
    const handleCreateQueryParams = useCallback((queryData, searchPage, searchSize) => {
        if (!createQueryParams)
            return;
        // Preserve query params that aren't managed by SourSearch or nuqs (like 'id', etc.)
        const preservedParams = {};
        // Params managed by SourSearch context
        const sourSearchParams = ['sourQuery', 'searchPage', 'searchSize'];
        // Params managed by nuqs in the consuming component (OrderListContainer, etc.)
        // These should NOT be manually preserved as nuqs handles them automatically
        const nuqsManagedParams = ['page', 'pageSize', 'tab'];
        // Use tabParam state from nuqs instead of reading from URL directly
        // This ensures we get the most recent state value, not a stale URL value
        if (tabParam) {
            preservedParams['tab'] = tabParam;
            tabParamRef.current = tabParam;
        }
        // Read other params from URL
        const currentParams = new URLSearchParams(window.location.search);
        // Preserve other params from URL (id, etc.) but NOT nuqs-managed params
        // nuqs automatically preserves params from other useQueryState hooks
        currentParams.forEach((value, key) => {
            if (!sourSearchParams.includes(key) && !nuqsManagedParams.includes(key)) {
                preservedParams[key] = value;
            }
        });
        const filterCount = countFilterFields(queryData);
        if ((filterCount.totalCount === 0 && !queryData.query?.query) ||
            (createQueryParams.removeOn === 'empty_string_inactive_filter' &&
                !queryData?.query?.query &&
                handleCheckIfEqualToDefault(queryData, defaultFilters))) {
            setQueryParams({ sourQuery: null, searchPage: initialValue.page, searchSize: searchSize });
        }
        else {
            setQueryParams({
                sourQuery: createQueryParams.method === 'with_filter' ? (filterCount.totalCount > 0 ? queryData : null) : queryData,
                ...(createQueryParams.stopPageAdd ? {} : { searchPage: searchPage }),
                searchSize: searchSize,
            });
        }
        // Restore preserved params after setQueryParams updates the URL
        if (Object.keys(preservedParams).length > 0) {
            const currentUrl = new URL(window.location.href);
            Object.entries(preservedParams).forEach(([key, value]) => {
                currentUrl.searchParams.set(key, value);
            });
            window.history.replaceState(null, '', currentUrl.toString());
        }
    }, [createQueryParams, setQueryParams, defaultFilters, initialValue.page, tabParam]);
    // --- end handleCreateQueryParams logic ---
    const handleSubmit = async (data, pagination) => {
        const queryData = { ...data };
        if (environmentOptions.application === undefined || environmentOptions.environment === undefined) {
            return;
        }
        if (filtersOpen)
            setFiltersOpen(false);
        const isSearchActive = queryData.query?.query ? true : false;
        const isFilterActive = handleCheckIfEqualToDefault(queryData, defaultFilters) && !defaultFilters?.filterOnDefaults
            ? false
            : countFilterFields(queryData).totalCount > 0;
        const searchSize = pagination?.passedSize !== undefined ? pagination.passedSize : size;
        const searchPage = pagination?.passedPage !== undefined ? pagination.passedPage : page;
        // --- createQueryParams logic ---
        handleCreateQueryParams(queryData, searchPage, searchSize);
        // --- end createQueryParams logic ---
        // CHECK TO SEE IF ANY FILTERS OR SEARCH SHOULD BE ACTIVE
        setSourQueryStatus({
            searchActive: isSearchActive,
            filterActive: isFilterActive,
            searchOrFilterActive: isSearchActive || isFilterActive,
            sourQuery: queryData,
            activeSourQuery: queryData,
            error: null,
        });
        const parsedQuery = handlePrepareQuery(queryData.query, environmentOptions.application);
        // PREPARE QUERY FOR ELASTIC
        // Merge user filters with access control filters (hidden from UI)
        // Look up access control queries from the map based on the query type
        const userFilters = removeUnusedFiltersAndConvertToArray(queryData.filters);
        const appHiddenFilters = appAccessFilters && data?.type && appAccessFilters[data?.type]
            ? removeUnusedFiltersAndConvertToArray(appAccessFilters[data?.type])
            : [];
        const providerHiddenFilters = !pagination?.accessControlQueries && providerAccessFilters && data?.type && providerAccessFilters[data?.type]
            ? removeUnusedFiltersAndConvertToArray(providerAccessFilters[data?.type])
            : [];
        const submitHiddenFilters = pagination?.accessControlQueries
            ? removeUnusedFiltersAndConvertToArray(pagination.accessControlQueries)
            : [];
        const combinedFilters = [...userFilters, ...appHiddenFilters, ...providerHiddenFilters, ...submitHiddenFilters];
        const query = {
            ...queryData,
            filters: combinedFilters,
            sort: queryData.sort?.field ? { field: queryData.sort.field, direction: queryData.sort.direction } : undefined,
            query: parsedQuery.query,
        };
        const elasticQuery = toElasticQuery(query, {
            application: environmentOptions.application,
            environment: environmentOptions.environment,
            // customer-portal restricts by customer (assuming Sourgum and Wayste customers are separate),
            // while Wayste hauler accounts as well as the SAP subscription list restrict by hauler
            haulerID: environmentOptions.application !== 'customer-portal' ? restriction : undefined,
            customerID: environmentOptions.application === 'customer-portal' ? restriction : undefined,
            multiMatchType: parsedQuery.multiMatchType,
        });
        const requestPath = Array.isArray(elasticQuery.index) ? elasticQuery.index.join(',') : elasticQuery.index || '';
        const isReceivableOrPayable = requestPath.includes('receivable') || requestPath.includes('payable');
        const requestFields = isReceivableOrPayable ? ['invoiceDetails.status_computed'] : undefined;
        try {
            // If onBackendProxyRequest callback is provided, use it instead of direct ES call
            if (onBackendProxyRequest) {
                const elasticsearchQuery = {
                    query: elasticQuery.query,
                    aggs: elasticQuery.aggs,
                    sort: elasticQuery.sort,
                    size: searchSize,
                    from: searchSize * searchPage,
                    ...(requestFields && { fields: requestFields }),
                };
                const hydratedResults = await onBackendProxyRequest(elasticsearchQuery, searchPage, searchSize);
                // Handle array of hydrated objects
                if (Array.isArray(hydratedResults)) {
                    const esResponse = transformArrayToEsResponse(hydratedResults, requestPath);
                    return buildSearchResponse(esResponse, hydratedResults.length, searchPage, searchSize, queryData.type);
                }
                // Handle ES format response
                if (hydratedResults && !Array.isArray(hydratedResults) && 'hits' in hydratedResults) {
                    const esResponse = hydratedResults;
                    const totalHits = extractTotalHitsFromEsResponse(esResponse);
                    return buildSearchResponse(esResponse, totalHits, searchPage, searchSize, queryData.type);
                }
                // Fallback: empty result
                const emptyResponse = createEmptyEsResponse();
                return buildSearchResponse(emptyResponse, 0, searchPage, searchSize, queryData.type);
            }
            // Direct Elasticsearch call (original behavior)
            const esUrl = `${environmentOptions.searchApiBaseUrl}/${requestPath}/_search`;
            const searchResults = await axios.post(esUrl, {
                query: elasticQuery.query,
                aggs: elasticQuery.aggs,
                sort: elasticQuery.sort,
                ...(requestFields && { fields: requestFields }),
            }, {
                headers: {
                    Authorization: `ApiKey ${environmentOptions.apiKey}`,
                },
                params: {
                    size: searchSize,
                    from: searchSize * searchPage,
                },
            });
            const totalHits = isNumberStrict(searchResults.data.hits.total)
                ? searchResults.data.hits.total
                : searchResults.data?.hits?.total?.value || 0;
            const totalPages = Math.ceil(totalHits / searchSize);
            return {
                results: searchResults.data,
                page: searchPage,
                size: searchSize,
                totalPages,
                totalHits,
                type: queryData.type,
            };
        }
        catch (error) {
            console.warn('onSubmit ERROR', error);
            // Set error
            setSourQueryStatus({
                ...sourQueryStatus,
                error: 'Something went wrong fetching results, please try again. If the issue persists please contact support.',
            });
            return;
        }
    };
    // PREPARE QUERY FOR ELASTIC && EXPOSES INITIAL RESULTS TO EXTERNAL COMPONENTS
    const onSubmit = async (data, options) => {
        setIsLoading(true);
        try {
            setPage(options?.passedPage || initialValue.page);
            setSize(options?.passedSize || initialValue.size);
            const response = await handleSubmit(data, {
                passedPage: options?.passedPage || initialValue.page,
                passedSize: options?.passedSize || initialValue.size,
                accessControlQueries: options?.accessControlQueries,
            });
            setSearchResponse(response);
            if (response && onResultsHandler)
                onResultsHandler({ ...response });
        }
        finally {
            setIsLoading(false);
        }
    };
    // FETCHES MORE RESULTS BASED ON CURRENT QUERY && EXPOSES PAGINATED > 0 RESULTS TO EXTERNAL COMPONENTS
    const handleGetMoreResults = async (passedPage, passedSize) => {
        setIsLoading(true);
        try {
            const response = await handleSubmit(sourQueryStatus.sourQuery, { passedPage, passedSize });
            setSearchResponse(response);
            if (response && onResultsHandler) {
                onResultsHandler({ ...response });
            }
        }
        finally {
            setIsLoading(false);
        }
    };
    const handlePaginationChange = (pagination) => {
        const { page: passedPage, size: passedSize } = pagination;
        if (passedPage === page && (!passedSize || passedSize === size)) {
            return;
        }
        setPage(passedPage || initialValue.page);
        if (passedSize)
            setSize(passedSize);
        handleGetMoreResults(passedPage || initialValue.page, passedSize || size);
    };
    const updateDefaultFilters = (value) => {
        setDefaultFilters(value);
        handleSourSearchSetup(defaultFilters?.query?.type, {
            filters: defaultFilters?.query?.filters,
            sort: defaultFilters?.query?.sort,
            query: defaultFilters?.query?.query,
        });
    };
    const handleInitialLoad = () => {
        const defaultQueryType = defaultFilters?.query?.type;
        const shouldLockQueryType = Boolean(defaultFilters?.lockFilterType && defaultQueryType);
        const queryMatchesDefaultType = !shouldLockQueryType || sourQuery?.type === defaultQueryType;
        if (createQueryParams && sourQuery && !queryMatchesDefaultType) {
            setQueryParams({
                sourQuery: null,
                searchPage: initialValue.page,
                searchSize: initialValues?.size ?? initialValue.size,
            });
        }
        // If sourQuery exists in URL, use it (user has applied filters/search)
        if (createQueryParams && sourQuery && queryMatchesDefaultType) {
            let newQuery = sourQuery.query;
            if (showEasyFilter &&
                sourQuery.query?.query &&
                typeof sourQuery.query.query === 'string' &&
                sourQuery.query.query.includes(':')) {
                const inputArray = [
                    sourQuery.query.query.slice(0, sourQuery.query.query.indexOf(':')),
                    sourQuery.query.query.slice(sourQuery.query.query.indexOf(':') + 1),
                ];
                const searchValue = inputArray[1];
                setSearchTerm(searchValue);
                newQuery = {
                    query: searchValue,
                    restrictToFields: sourQuery.query.restrictToFields,
                };
            }
            else {
                if (sourQuery.query?.query)
                    setSearchTerm(sourQuery.query.query);
            }
            const newSourQuery = handleSourSearchSetup(sourQuery.type, {
                filters: sourQuery.filters,
                sort: sourQuery.sort,
                query: newQuery,
            });
            if (newSourQuery) {
                onSubmit(newSourQuery, { passedPage: nuqsPage, passedSize: nuqsSize });
            }
        }
        else if (defaultFilters && defaultFilters.query) {
            // Call onSubmit from defaultFilters on initial load
            // This only runs once on mount (no remounting since key prop was removed)
            const newSourQuery = handleSourSearchSetup(defaultFilters?.query?.type, {
                filters: defaultFilters?.query?.filters,
                sort: defaultFilters?.query?.sort,
                query: defaultFilters?.query?.query,
            });
            if (newSourQuery) {
                onSubmit(newSourQuery);
            }
        }
    };
    const focusSearch = () => {
        if (searchInputRef.current) {
            // This ensures the input is focused and the cursor is placed at the end of the value
            setTimeout(() => {
                const input = searchInputRef.current;
                input.focus();
                // Move cursor to the end
                if (typeof input.selectionStart === 'number' && typeof input.selectionEnd === 'number') {
                    const length = input.value.length;
                    input.setSelectionRange(length, length);
                }
            }, 50); //Slight delay to ensure the input is focused, hacky but works since something seems to be messing with the focus
        }
    };
    // reruns the last search. Useful for when the table is editable and the data changes
    const rerunLastSearch = useCallback(() => {
        const queryToRerun = sourQueryStatus.activeSourQuery || sourQueryStatus.sourQuery;
        if (queryToRerun && queryToRerun.type) {
            onSubmit(queryToRerun, { passedPage: page, passedSize: size });
        }
    }, [sourQueryStatus.activeSourQuery, sourQueryStatus.sourQuery, onSubmit, page, size]);
    // SETS STARTING VALUES BASED ON QUERY STRING OR DEFAULT FILTERS
    useEffect(() => {
        handleInitialLoad();
    }, []);
    useEffect(() => {
        const defaultQueryType = defaultFilters?.query?.type;
        if (!createQueryParams ||
            !sourQuery ||
            !defaultFilters?.lockFilterType ||
            !defaultQueryType ||
            sourQuery.type === defaultQueryType) {
            return;
        }
        setSearchTerm('');
        setQueryParams({
            sourQuery: null,
            searchPage: initialValue.page,
            searchSize: initialValues?.size ?? initialValue.size,
        });
    }, [createQueryParams, defaultFilters?.lockFilterType, defaultFilters?.query?.type, initialValues?.size, setQueryParams, sourQuery]);
    // ON INIT APPLICATION
    useEffect(() => {
        if (environmentOptions.application) {
            const applicationTypes = extractAllowedTypes(environmentOptions.application);
            const types = [];
            for (let i = 0; i < applicationTypes.length; i++) {
                types.push(SearchSchema[applicationTypes[i]]);
            }
            setFilterRestrictions(types);
        }
    }, [environmentOptions.application]);
    const values = useMemo(() => ({
        environmentOptions,
        onSubmit,
        onNavigate: externalNavigate,
        isLoading,
        filtersOpen,
        setFiltersOpen,
        defaultFilters,
        restriction,
        highlightActive,
        handleCheckIfEqualToDefault,
        onSeeAllClick,
        onGlobalOnSeeAllClick,
        registerOnResultsHandler,
        page,
        size,
        searchResponse,
        showEasyFilter,
        searchTerm,
        setSearchTerm,
        filterRestrictions,
        sourQueryStatus,
        handlePaginationChange,
        filterableAttributes,
        getAttributeAsFilter,
        handleSourSearchSetup,
        handleUpdateSourQuery,
        updateDefaultFilters,
        focusSearch,
        searchInputRef,
        rerunLastSearch,
    }), [
        environmentOptions,
        externalNavigate,
        filtersOpen,
        setFiltersOpen,
        isLoading,
        defaultFilters,
        restriction,
        highlightActive,
        handleCheckIfEqualToDefault,
        page,
        size,
        searchResponse,
        showEasyFilter,
        searchTerm,
        filterRestrictions,
        sourQueryStatus,
        handlePaginationChange,
        filterableAttributes,
        getAttributeAsFilter,
        handleSourSearchSetup,
        handleUpdateSourQuery,
        rerunLastSearch,
    ]);
    return _jsx(SourSearchContext.Provider, { value: values, children: children });
};
//# sourceMappingURL=sour-search-context.js.map